import { fileInfoType, propertyType } from 'api/file/interfaces/get-file';
//import { iNameOption } from 'api/jackets/interfaces';
import { IVerifyAddressResponse } from 'api/file/interfaces/verify-address';
import { mortgageClause, MortgageClauseTextOptions } from './parties/form/MortgageClause';
import { IGetLenderResponse } from './parties/interfaces/CreateLenderRequest';
import { ILenderFirm } from './parties/interfaces/LenderModels';

/** County is defined as the county of the first property in the file's properties array. */
export function getPropertyCounty(fileData: fileInfoType): string {
  if (fileData.properties && fileData.properties.length > 0) return fileData.properties[0].county || '';
  return '';
}

// export function getLenderNames(fileData: fileInfoType): iNameOption[] {
//   if (!fileData.lenders) return [];
//   return fileData.lenders.map((lender: IGetLenderResponse) => {
//     return { name: lender.name };
//   });
// }

interface iLenderOption {
  name: string;
  value: number;
}

/** Does given lender id exist in the list of lender options? */
export function lenderExistsInList(lenderId: number, lenderOptions: iLenderOption[]): boolean {
  const matched = lenderOptions.find((l) => l.value === lenderId);
  return !!matched;
}

export function updateLenderWithFirm(
  lender: IGetLenderResponse | undefined,
  lenderFirm: ILenderFirm | undefined,
): IGetLenderResponse {
  if (lender && lenderFirm) {
    const {
      AddressBookEntryId,
      LenderAttentionName,
      LenderClauseType,
      LenderClauseText,
      PhoneNumber,
      EmailAddress,
      Address,
      Name,
    } = lenderFirm || {};
    const { Address1, Address2, City, County, PostalCode, State } = Address || {};
    return {
      ...lender,
      lenderAddressBookId: AddressBookEntryId,
      attention: LenderAttentionName || '',
      lenderClauseType: LenderClauseType || '',
      lenderClauseText: LenderClauseText || '',
      phoneNumber: PhoneNumber || '',
      emailAddress: EmailAddress || '',
      address: {
        address1: Address1 || '',
        address2: Address2 || '',
        city: City || '',
        county: County || '',
        postalCode: PostalCode || '',
        state: State || '',
      },
      name: Name || '',
      lenderId: lender.lenderId,
    };
  } else return { ...lender };
}

export function getResetLender(lender: IGetLenderResponse, isCreateForm: boolean): IGetLenderResponse {
  return {
    ...lender,
    name: '',
    attention: '',
    loanNumber: '',
    loanAmount: 0,
    lenderClauseType: isCreateForm ? mortgageClause[0].name : '',
    lenderClauseText: isCreateForm ? MortgageClauseTextOptions[mortgageClause[0].name].value : '',
    emailAddress: '',
    phoneNumber: '',
    lenderTypeOptions: '',
    address: {
      address1: '',
      address2: '',
      city: '',
      postalCode: '',
      state: '',
    },
  };
}

export function isPropertyReportExceptionState(state: string): boolean {
  const exceptionStates: Array<string> = ['ID', 'MT'];
  return exceptionStates.some((s) => s === state);
}

/**
 * Normalizes an address string by expanding common abbreviations to their full forms
 * @param address The address string to normalize
 * @returns The normalized address string
 */
function normalizeAddress(address: string): string {
  if (!address) return '';

  let normalized = address.toLowerCase().trim();

  // Remove periods first
  normalized = normalized.replace(/\./g, '');

  // Define abbreviation mappings - only abbreviations that need expansion
  const abbreviationMap: { [key: string]: string } = {
    // Directional abbreviations
    '\\bn\\b': 'north',
    '\\bno\\b': 'north',
    '\\bnth\\b': 'north',
    '\\bs\\b': 'south',
    '\\bso\\b': 'south',
    '\\bsth\\b': 'south',
    '\\be\\b': 'east',
    '\\bea\\b': 'east',
    '\\best\\b': 'east',
    '\\bw\\b': 'west',
    '\\bwe\\b': 'west',
    '\\bwst\\b': 'west',
    '\\bne\\b': 'northeast',
    '\\bnw\\b': 'northwest',
    '\\bse\\b': 'southeast',
    '\\bsw\\b': 'southwest',

    // Street type abbreviations
    '\\bst\\b': 'street',
    '\\bstr\\b': 'street',
    '\\bave\\b': 'avenue',
    '\\bav\\b': 'avenue',
    '\\bblvd\\b': 'boulevard',
    '\\bbl\\b': 'boulevard',
    '\\bdr\\b': 'drive',
    '\\brd\\b': 'road',
    '\\bct\\b': 'court',
    '\\bcir\\b': 'circle',
    '\\bpl\\b': 'place',
    '\\bln\\b': 'lane',
    '\\bpkwy\\b': 'parkway',
    '\\bpky\\b': 'parkway',
    '\\bter\\b': 'terrace',
    '\\btrl\\b': 'trail',
    '\\bhwy\\b': 'highway',
    '\\bfwy\\b': 'freeway',
    '\\bexpy\\b': 'expressway',
    '\\bsq\\b': 'square',
    '\\bpk\\b': 'park',
    '\\bpt\\b': 'point',
    '\\brdg\\b': 'ridge',
    '\\bcrk\\b': 'creek',
    '\\bxing\\b': 'crossing',

    // Unit type abbreviations
    '\\bapt\\b': 'apartment',
    '\\bste\\b': 'suite',
    '\\bunit\\b': 'unit',
    '\\bbldg\\b': 'building',
    '\\bfl\\b': 'floor',
    '\\brm\\b': 'room',
  };

  // Apply replacements
  Object.entries(abbreviationMap).forEach(([pattern, replacement]) => {
    const regex = new RegExp(pattern, 'gi');
    normalized = normalized.replace(regex, replacement);
  });

  // Handle number patterns
  normalized = normalized.replace(/#\s*/g, 'number ');
  normalized = normalized.replace(/\bno\s+/g, 'number ');

  // Remove extra spaces and normalize whitespace
  normalized = normalized.replace(/\s+/g, ' ').trim();

  return normalized;
}

/**
 * Compares two address strings for equality, handling common abbreviations
 * @param entered First address to compare
 * @param verified Second address to compare
 * @returns True if addresses are equivalent, false otherwise
 */
export function compareAddressesWithAbbreviations(entered: string | undefined, verified: string | undefined): boolean {
  if (!entered && !verified) return true;
  if (!entered || !verified) return false;

  const enteredNormalized = normalizeAddress(entered);
  const verifiedNormalized = normalizeAddress(verified);

  return enteredNormalized === verifiedNormalized;
}

export function apnVerifyCheckForMatch(
  propertyData: propertyType,
  verifyAddressResults: IVerifyAddressResponse,
): boolean {
  return (
    (!propertyData.address1 && !propertyData.address2 && !propertyData.city && !propertyData.postalCode) ||
    (compareAddressesWithAbbreviations(propertyData.address1, verifyAddressResults.address1) &&
      compareAddressesWithAbbreviations(propertyData.address2, verifyAddressResults.address2) &&
      propertyData.city?.toLowerCase() === verifyAddressResults.city?.toLowerCase() &&
      ((propertyData.postalCode &&
        propertyData.postalCode.length > 5 &&
        verifyAddressResults.zip === propertyData.postalCode) ||
        propertyData.postalCode === verifyAddressResults.zip.substring(0, 5)) &&
      propertyData.apn === verifyAddressResults.apn)
  );
}

export function addressVerifyCheckForMatch(
  propertyData: propertyType,
  verifyAddressResults: IVerifyAddressResponse,
): boolean {
  return (
    compareAddressesWithAbbreviations(verifyAddressResults.address1, propertyData.address1) &&
    compareAddressesWithAbbreviations(verifyAddressResults.address2, propertyData.address2) &&
    verifyAddressResults.city?.toLowerCase() === propertyData.city?.toLowerCase() &&
    verifyAddressResults.stateCode?.toLowerCase() === propertyData.state?.toLowerCase() &&
    verifyAddressResults.county?.toLowerCase() === propertyData.county?.toLowerCase() &&
    ((propertyData.postalCode &&
      propertyData.postalCode.length > 5 &&
      verifyAddressResults.zip === propertyData.postalCode) ||
      propertyData.postalCode === verifyAddressResults.zip.substring(0, 5)) &&
    (!propertyData.apn || verifyAddressResults.apn === propertyData.apn)
  );
}
